import { Injectable, inject } from '@angular/core';
import { Apollo, ApolloBase } from 'apollo-angular';
import { environment } from '../../../../environments/environment';
import { GraphQLMutations } from './mutations.graphql';
import { GraphQLQueries } from './queries.graphql';
import { GraphQLInputTypes, GraphQLTypes } from './types.graphql';
import { of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PlatformGraphQlService {
  private _apollo = inject(Apollo);

  private _platformBrandId = environment.platform_brandId;

  /**
   * For API communication
   */
  private _apolloApi: ApolloBase = this._apollo.use('PlatformApi');

  public get apolloApi() {
    return this._apolloApi;
  }

  public get platformBrandId(): string {
    return this._platformBrandId;
  }

  /**
   * Mutate users sign up by email
   * @param {RegisterPlayerByEmailInput} data - The `data` parameter is an object of type
   */
  public usersSignUp(data: GraphQLInputTypes.RegisterPlayerByEmailInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.SIGN_UP_BY_EMAIL,
      variables: {
        registerPlayerByEmailInput: {
          brandId: this.platformBrandId,
          ...data,
        },
      },
    });
  }

  /**
   * Mutate users sign in
   * @param {LoginPlayerByEmailInput} params - The `params` parameter is an object of type
   */
  public usersSignIn(params: GraphQLInputTypes.LoginPlayerByEmailInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.SIGN_IN_BY_EMAIL,
      variables: {
        loginPlayerByEmailInput: {
          brandId: this.platformBrandId,
          ...params,
        },
      },
    });
  }

  /**
   * Mutate users logout
   */
  public userLogout() {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.LOGOUT,
      variables: {},
    });
  }

  /**
   * Mutate reset users password
   * @param {ResetPlayerPasswordInput} data - The `data` parameter is an object of type
   */
  public resetUsersPassword(data: GraphQLInputTypes.ResetPlayerPasswordInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.RESET_PASSWORD_BY_EMAIL,
      variables: {
        resetPlayerPasswordInput: {
          brandId: this.platformBrandId,
          ...data,
        },
      },
    });
  }

  /**
   * Mutate update users password
   * @param {UpdatePlayerPasswordByTokenInput} data - The `data` parameter is an object of type
   */
  public updateUsersPassword(data: GraphQLInputTypes.UpdatePlayerPasswordByTokenInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.UPDATE_USER_PASSWORD_BY_TOKEN,
      variables: {
        updatePlayerPasswordByTokenInput: {
          ...data,
        },
      },
    });
  }

  /**
   * Mutate update users info
   * @param {UpdatePlayerInput} data - The `data` parameter is an object of type
   */
  public updateUsersInfo(data: GraphQLInputTypes.UpdatePlayerInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.UPDATE_USER_PROFILE,
      variables: {
        updatePlayerInput: {
          ...data,
        },
      },
    });
  }

  /**
   * Update user phone number using GraphQL mutation.
   */
  public updateUserPhone(data: string) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.UPDATE_USER_PHONE,
      variables: {
        updatePlayerInput: {
          phone: data,
        },
      },
    });
  }

  /**
   * Mutate update user recieve sms
   * @param {UpdatePlayerInput} data - The `data` parameter is an object of type
   */
  public updateUserRecieveSms(data: GraphQLInputTypes.UpdatePlayerInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.UPDATE_USER_RECEIVE_SMS,
      variables: {
        updatePlayerInput: {
          ...data,
        },
      },
    });
  }

  /**
   * Mutate update user recieve email
   * @param {UpdatePlayerInput} data - The `data` parameter is an object of type
   */
  public updateUserRecieveEmail(data: GraphQLInputTypes.UpdatePlayerInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.UPDATE_USER_RECEIVE_EMAIL,
      variables: {
        updatePlayerInput: {
          ...data,
        },
      },
    });
  }

  /**
   * Mutate update users address
   * @param {UpdatePlayerAddressInput} data - The `data` parameter is an object of type
   */
  public updateUsersAddress(data: GraphQLInputTypes.UpdatePlayerAddressInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.UPDATE_USER_ADDRESS,
      variables: {
        updatePlayerAddressInput: {
          ...data,
        },
      },
    });
  }

  /**
   * Query user info
   */
  public userInfo(networkOnly: boolean = false) {
    return this._apolloApi.watchQuery({
      fetchPolicy: networkOnly ? 'network-only' : 'cache-and-network',
      query: GraphQLQueries.USER_INFO,
      variables: {
        brandId: this.platformBrandId,
      },
    }).valueChanges;
  }

  public kycRequests(networkOnly: boolean = false) {
    return this._apolloApi.watchQuery({
      fetchPolicy: networkOnly ? 'network-only' : 'cache-and-network',
      query: GraphQLQueries.KYC_REQUESTS,
      variables: {
        brandId: this.platformBrandId,
        filter: {},
      },
    }).valueChanges;
  }

  public getKycToken(kycRequestId: string) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.KYC_ACCESS_TOKEN,
      variables: {
        kycRequestId,
      },
    });
  }

  /**
   * Query currency list
   */
  public currencyList() {
    return this._apolloApi.watchQuery({
      fetchPolicy: 'cache-and-network',
      query: GraphQLQueries.SITE_CURRENCY_LIST,
      variables: {
        brandId: this.platformBrandId,
      },
    }).valueChanges;
  }

  /**
   * Query user payment info
   */
  public userPaymentInfo(networkOnly: boolean = false) {
    return this._apolloApi.watchQuery({
      fetchPolicy: networkOnly ? 'network-only' : 'cache-and-network',
      query: GraphQLQueries.USER_PAYMENT_INFO,
      variables: {
        brandId: this.platformBrandId,
      },
    }).valueChanges;
  }

  /**
   * Query payment method list by wallet
   */
  public paymentMethodListByWallet(walletId: string, paymentTransactionType: GraphQLTypes.SitePaymentTransactionType) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.PAYMENT_METHOD_LIST,
      variables: {
        walletId,
        paymentTransactionType,
      },
    });
  }

  /**
   * Query payment transaction list
   */
  public paymentTransactionList(paymentTransactionListInput: GraphQLInputTypes.PaymentTransactionListInput = {}) {
    return this._apolloApi.watchQuery({
      fetchPolicy: 'cache-and-network',
      query: GraphQLQueries.PAYMENT_TRANSACTION_LIST,
      variables: {
        brandId: this.platformBrandId,
        ...paymentTransactionListInput,
      },
    }).valueChanges;
  }

  /**
   * Query payment transaction item
   */
  public paymentTransactionItem(paymentTransactionItemInput: GraphQLInputTypes.PaymentTransactionItemInput = {}) {
    return this._apolloApi.watchQuery({
      fetchPolicy: 'cache-and-network',
      query: GraphQLQueries.PAYMENT_TRANSACTION_ITEM,
      variables: {
        brandId: this.platformBrandId,
        ...paymentTransactionItemInput,
      },
    }).valueChanges;
  }

  /**
   * Mutate create new wallet
   * @param {CreateSiteWalletInput} data - The `data` parameter is an object of type
   */
  public createWallet(data: GraphQLInputTypes.CreateSiteWalletInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.CREATE_WALLET,
      variables: {
        createSiteWalletInput: {
          ...data,
        },
      },
    });
  }

  /**
   * Query wallet info by id
   */
  public walletInfo(data: GraphQLInputTypes.WalletInfoInput) {
    return this._apolloApi.watchQuery({
      fetchPolicy: 'cache-and-network',
      query: GraphQLQueries.WALLET_INFO,
      variables: {
        brandId: this.platformBrandId,
        ...data,
      },
    }).valueChanges;
  }

  /**
   * Mutate set new wallet
   * @param {WalletInfoInput} data - The `data` parameter is an object of type
   */
  public setWallet(data: GraphQLInputTypes.WalletInfoInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.SET_WALLET,
      variables: {
        brandId: this.platformBrandId,
        ...data,
      },
    });
  }

  public createDepositSession(data: GraphQLInputTypes.CreateDepositTransactionInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.CREATE_DEPOSIT_SESSION,
      variables: {
        CreateDepositTransactionInput: {
          ...data,
        },
      },
    });
  }

  public createWithdrawalSession(data: GraphQLInputTypes.CreateWithdrawalTransactionInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.CREATE_WITHDRAWAL_SESSION,
      variables: {
        CreateWithdrawalTransactionInput: {
          ...data,
        },
      },
    });
  }

  public cancelWithdrawal(data: GraphQLInputTypes.CancelWithdrawalInfoInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.CANCEL_WITHDRAWAL,
      variables: {
        ...data,
      },
    });
  }

  /**
   * Mutate set token
   * @param {TokenInput} data - The `data` parameter is an object of type
   */
  public setToken(data: GraphQLInputTypes.TokenInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.CONFIRM_EMAIL_BY_TOKEN,
      variables: {
        brandId: this.platformBrandId,
        ...data,
      },
    });
  }

  /**
   * Query game transaction list
   */
  public gameTransactionList(gameTransactionListInput: GraphQLInputTypes.GameTransactionListInput = {}) {
    return this._apolloApi.watchQuery({
      fetchPolicy: 'cache-and-network',
      query: GraphQLQueries.BET_TRANSACTION_LIST,
      variables: {
        brandId: this.platformBrandId,
        ...gameTransactionListInput,
      },
    }).valueChanges;
  }

  public createGameSession(data: GraphQLInputTypes.GameSessionLauncherInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.CREATE_GAME_SESSION,
      variables: {
        ...data,
      },
    });
  }

  public createDemoGameSession(data: GraphQLInputTypes.GameSessionLauncherInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.CREATE_DEMO_GAME_SESSION,
      variables: {
        ...data,
      },
    });
  }

  /**
   * Query bonus list.
   * @param networkOnly Whether to fetch from network only or allow cache.
   */
  public bonusList(networkOnly: boolean = false) {
    return this._apolloApi.watchQuery({
      fetchPolicy: networkOnly ? 'network-only' : 'cache-and-network',
      query: GraphQLQueries.BONUS_LIST,
      variables: {
        brandId: this.platformBrandId,
        walletsPagination: {
          limit: 20,
        },
        bonusPagination: {
          limit: 500,
        },
      },
    }).valueChanges;
  }

  /**
   * Updates the status of a bonus.
   * @param data - The input data for the mutation.
   */
  public updateBonusStatus(data: GraphQLInputTypes.UpdateBonusStatusInput) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.UPDATE_BONUS_STATUS,
      variables: {
        ...data,
      },
    });
  }

  /**
   * Mutate update users language
   */
  public updateUsersLang(language: string) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.UPDATE_USER_LANG,
      variables: {
        updatePlayerInput: {
          language,
        },
      },
    });
  }

  /**
   * Mutate add users segment
   */
  public addUsersSegment(segmentId: string) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.ADD_USER_SEGMENT,
      variables: {
        segmentId,
      },
    });
  }

  /**
   * Mutate delete users segment
   */
  public deleteUsersSegment(segmentId: string) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.DELETE_USER_SEGMENT,
      variables: {
        segmentId,
      },
    });
  }

  /**
   * Query last played games list.
   * @param networkOnly Whether to fetch from network only or allow cache.
   */
  public lastPlayedGamesList(networkOnly: boolean = false) {
    const now = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    return this._apolloApi.watchQuery({
      fetchPolicy: networkOnly ? 'network-only' : 'cache-and-network',
      query: GraphQLQueries.LAST_PLAYED_GAMES,
      variables: {
        brandId: this.platformBrandId,
        siteGameSessionFilterInput: {
          createdAt: {
            value: null,
            values: [
              lastMonth.toISOString(),
              now.toISOString(),
            ],
            command: 'BETWEEN',
          },
          uniqueGame: {
            value: 'true',
            values: null,
            command: 'EQUALS',
          },
        },
        siteGameSessionSortInput: {
          'createdAt': 'DESC',
        },
        paginationInput: {
          limit: 20,
        },
      },
    }).valueChanges;
  }

  public playerRestrictions(networkOnly: boolean = false) {
    return this._apolloApi.watchQuery({
      fetchPolicy: networkOnly ? 'network-only' : 'cache-and-network',
      query: GraphQLQueries.PLAYER_RESTRICTIONS,
    }).valueChanges;
  }

  /**
   * Query lootbox list.
   * @param networkOnly Whether to fetch from network only or allow cache.
   */
  public lootboxList(networkOnly: boolean = false) {
    return this._apolloApi.watchQuery({
      fetchPolicy: networkOnly ? 'network-only' : 'cache-and-network',
      query: GraphQLQueries.LOOTBOX_LIST,
      variables: {
        brandId: this.platformBrandId,
        paginationInput: {
          limit: 500,
        },
      },
    }).valueChanges;
  }

  /**
   * Mutate open lootbox
   */
  public openLootbox(siteLootboxOpenId: string, walletId: string) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.OPEN_LOOTBOX,
      variables: {
        siteLootboxOpenId,
        walletId,
      },
    });
  }

  /**
   * Mutate cancel lootbox
   */
  public cancelLootbox(siteLootboxCancelId: string) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.CANCEL_LOOTBOX,
      variables: {
        siteLootboxCancelId,
      },
    });
  }

  /**
   * Mutate request OTP
   */
  public requestOTP() {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.REQUEST_OTP,
    });
  }

  /**
   * Mutate verify OTP
   */
  public veryfyOPT(code: string) {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.VERIFY_OTP,
      variables: {
        verifyOtpInput: {
          code,
        },
      },
    });
  }

  /**
   * Mutate request Email Verification
   */
  public requestEmailVerification() {
    return this._apolloApi.mutate({
      mutation: GraphQLMutations.REQUEST_EMAIL,
    });
  }

}
